import _ from 'lodash';
import ReactQuill from 'react-quill';
import { GrammarReplacement, Paraphrase } from '../augmented-writing.types';
import { TooltipContentType } from '../../../enums';
const { Quill } = ReactQuill;
const InlineBlot = Quill.import('blots/inline') as any;

export const GENERAL_HIGHLIGHT_CLASS = 'highlight-class';
export const GENERAL_SUGGESTION_CLASS = 'suggestion-class';
export const HEAVY_SUGGESTION_CLASS = 'heavy-suggestion-class';
export const GENERAL_DELETION_CLASS = 'deletion-class';
export const HEAVY_DELETION_CLASS = 'heavy-deletion-class';
export const GENERAL_INSERTION_CLASS = 'insertion-class';
export const HEAVY_INSERTION_CLASS = 'heavy-insertion-class';
export const GENERAL_GRAMMAR_CLASS = 'grammar-replacement-class';
export const GENERAL_PARAPHRASE_CLASS = 'paraphrase-replacement-class';

export interface HighlightValue {
  categories?: string[];
  suggestions?: Paraphrase | GrammarReplacement;
  contentType?: TooltipContentType;
  score_improvement?: number;
  highlightId?: string;
}

export class HighlightBlot extends InlineBlot {
  static blotName: string;
  static tagName: string;
  declare domNode: HTMLElement;
  declare statics: { blotName: string };

  public static create(value: HighlightValue) {
    const node: HTMLElement = super.create(value);
    if (
      !value ||
      (value.contentType !== TooltipContentType.HIGHLIGHT &&
        value.contentType !== TooltipContentType.GRAMMAR_REPLACEMENT &&
        value.contentType !== TooltipContentType.PARAPHRASE)
    ) {
      return node;
    }
    HighlightBlot.addAttributes(node, value);
    return node;
  }

  protected static formats(domNode: HTMLElement) {
    const contentType = domNode.dataset.contentType;
    const categories = domNode.dataset.categories;
    const grammarReplacements = domNode.dataset.grammarReplacements;
    const paraphrases = domNode.dataset.paraphrases;
    if (!categories && !contentType && !grammarReplacements && !paraphrases) {
      return undefined;
    }
    const formats: HighlightValue = {};
    if (contentType) {
      formats.contentType = JSON.parse(contentType);
    }
    if (grammarReplacements) {
      formats.suggestions = JSON.parse(grammarReplacements);
    }
    if (paraphrases) {
      formats.suggestions = JSON.parse(paraphrases);
    }
    if (categories) {
      formats.categories = JSON.parse(categories);
    }
    return formats;
  }

  // Custom helper
  private static addAttributes(node: HTMLElement, value: HighlightValue) {
    /*
      Used to add all attributes that are needed for the blot. The attributes carry
      all information about the formats as well as the classes for the styling

      Highlight:
        - add categories as classes
        - add general class for highlights
        - set categories data attribute with the category list
      Suggestion:
        - add general class for suggestions
        - add suggestions data attribute with the suggested words
    */
    if (value && value.categories && value.contentType === TooltipContentType.HIGHLIGHT) {
      const [first, ...rest] = [...value.categories.slice(0, 1), ...value.categories.slice(1)];
      if (first) {
        node.classList.add(first);
      }
      const restSecondary = _.map(rest, (e) => `secondary-${e}`);
      if (!_.isNil(restSecondary) && restSecondary.length > 0) {
        node.classList.add(...restSecondary);
      }
      node.classList.add(GENERAL_HIGHLIGHT_CLASS);
      node.dataset.categories = JSON.stringify(value.categories);
    }
    if (value && value.suggestions && value.contentType === TooltipContentType.PARAPHRASE) {
      node.classList.add(GENERAL_PARAPHRASE_CLASS);
      node.dataset.paraphrases = JSON.stringify(value.suggestions);
      if (value.highlightId) {
        node.id = value.highlightId;
      }
    }
    if (value && value.suggestions && value.contentType === TooltipContentType.GRAMMAR_REPLACEMENT) {
      node.classList.add(GENERAL_GRAMMAR_CLASS);
      node.dataset.grammarReplacements = JSON.stringify(value.suggestions);
      if (value.highlightId) {
        node.id = value.highlightId;
      }
    }
    if (value && value.contentType) {
      node.dataset.contentType = JSON.stringify(value.contentType);
    }
  }

  // Custom helper
  private static removeAttributes(node: HTMLElement, value?: HighlightValue) {
    /*
      Used to remove all attributes if an empty value is passed. This removes the attributes
      and the stylings. Afterwards the node is not a HighlightBlot anymore but only a normal span
    */
    const categories = node.dataset.categories;
    const grammarReplacement = node.dataset.grammarReplacements;
    const paraphrases = node.dataset.paraphrases;
    if (
      !value ||
      (value.contentType !== TooltipContentType.HIGHLIGHT &&
        value.contentType !== TooltipContentType.GRAMMAR_REPLACEMENT &&
        value.contentType !== TooltipContentType.PARAPHRASE)
    ) {
      delete node.dataset.contentType;
    }
    if ((!value || !value.categories) && categories) {
      // If categories are not set, we remove all highlight-related things
      const categoryArray = JSON.parse(categories);
      const secondaryCategories = _.map(categoryArray, (e) => `secondary-${e}`);
      node.classList.remove(...categoryArray);
      node.classList.remove(...secondaryCategories);
      node.classList.remove(GENERAL_HIGHLIGHT_CLASS);
      delete node.dataset.categories;
    }
    if ((!value || !value.suggestions) && grammarReplacement) {
      // If suggestions are not set, we remove all suggestion-related things
      node.classList.remove(GENERAL_GRAMMAR_CLASS);
      node.removeAttribute('id');
      delete node.dataset.grammarReplacements;
    }
    if ((!value || !value.suggestions) && paraphrases) {
      // If suggestions are not set, we remove all suggestion-related things
      node.classList.remove(GENERAL_PARAPHRASE_CLASS);
      node.removeAttribute('id');
      delete node.dataset.paraphrases;
    }
  }

  public format(name: any, value?: HighlightValue) {
    if (name === this.statics.blotName) {
      if (value) {
        HighlightBlot.addAttributes(this.domNode, value);
      } else {
        // No values are passed, we remove the formatting completely
        HighlightBlot.removeAttributes(this.domNode, value);
      }
    } else {
      super.format(name, value);
    }
  }
}
HighlightBlot.blotName = 'highlight';
HighlightBlot.tagName = 'span';

